import type { I18nMoneyFunction } from 'wanderu-i18n';

const formatFees = ({
    currency,
    fees = {},
}: {
    currency: string,
    fees: Record<string, number>,
}) => {
    return Object.entries(fees).map(([id, fee]) => {
        const label = id === 'wanderu_booking_fee'
            ? 'platform-fee'
            : 'carrier-fee';

        return {
            id,
            label,
            pricing: {
                [currency]: fee,
            },
        };
    });
};

const formatPrices = (
    {
        price,
        fees = {},
        currency,
    }: {
        price: number,
        fees: Record<string, number>,
        currency: string,
    },
): Record<string, number>[] => {
    const priceList = [{ [currency]: price }];

    for (const fee of Object.values(fees)) {
        priceList.push({
            [currency]: fee,
        });
    }

    return priceList;
};

const formatTaxes = (
    {
        taxes = {},
        currency,
    }: {
        taxes: Record<string, number>,
        currency: string,
    },
): Record<string, number>[] => {
    const taxList = [];

    for (const tax of Object.values(taxes)) {
        taxList.push({
            [currency]: tax,
        });
    }

    return taxList;
};

type PriceObject = Record<string, {
    price: number;
    fees: Record<string, number>;
    taxes: Record<string, number>;
}>;

export const formatPricing = (
    {
        i18nMoney,
        priceObject,
    }: {
        i18nMoney: I18nMoneyFunction,
        priceObject: PriceObject,
    }
) => {
    const currency = Object.keys(priceObject)[0];
    const pricing = priceObject?.[currency] || {};
    const { price, fees, taxes } = pricing;

    const basePrice = !!price && i18nMoney([{ [currency]: price }]).getTotalPrice();
    const formattedBasePrice = !!basePrice && i18nMoney.format(basePrice);

    const feesToList = formatFees({ currency, fees });
    const carrierFees = feesToList.filter((fee) => fee.label === 'carrier-fee');
    const platformFees = feesToList.filter((fee) => fee.label === 'platform-fee');

    const formattedTaxes = !!taxes && formatTaxes({ currency, taxes });
    const totalTaxes = !!formattedTaxes && i18nMoney(formattedTaxes).getTotalPrice();
    const formattedTotalTaxes = !!totalTaxes && i18nMoney.format(totalTaxes);

    const formattedPrice = !!price && formatPrices({ currency, fees, price });
    const totalPrice = !!formattedPrice && i18nMoney(formattedPrice).getTotalPrice();
    const formattedTotalPrice = !!totalPrice && i18nMoney.format(totalPrice);

    return {
        basePrice: formattedBasePrice,
        carrierFees,
        fees: feesToList,
        platformFees,
        taxes: formattedTotalTaxes,
        totalPrice: formattedTotalPrice,
    };
};
